/**
 * The narrow index: what is in the deck, what has an answer, and which card the desk is showing.
 *
 * **Nothing in this list ever moves.** An answered row is struck through where it stands
 * (`model/taskDeck`), so "3 z 14" has a denominator and the row under the trainer's pointer is the
 * row they were about to press. That is the whole difference from the Pulpit's bento, whose rows
 * leave and close the gap behind them.
 */

import { motion } from 'motion/react';
import { useEffect, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { ReasonTile, TaskGlyphMark } from '~/features/attention-queue';
import { BubbleGlyph } from '~/features/chat';
import { screenMotion, useMotionOnKeyChange } from '~/shared/design-system/motion';
import { CheckIcon } from '~/shared/ui/AuthIcons';
import { ElapsedMarkIcon } from '~/shared/ui/EmptyStateIcons';
import ghost from '~/shared/ui/ghostButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { DECK_SCOPES, type DeckCard, type DeckCounts, type DeckProgress, type DeckScope } from '../model/taskDeck';
import { INDEX_ROW_ATTRIBUTE } from '../lib/useTaskShortcuts';
import { UndoIcon } from './TaskIcons';
import styles from './taskQueue.module.css';

const SCOPE_LABEL_KEY: Readonly<Record<DeckScope, string>> = {
  all: 'web:tasks.queue.scopeAll',
  Triage: 'web:tasks.queue.scopeTriage',
  Feedback: 'web:tasks.queue.scopeFeedback',
};

interface TaskQueueIndexProps {
  readonly cards: readonly DeckCard[];
  readonly selectedKey: string | null;
  readonly scope: DeckScope;
  readonly counts: DeckCounts;
  readonly progress: DeckProgress;
  readonly onScope: (scope: DeckScope) => void;
  readonly onSelect: (key: string) => void;
  /** How long an item has been waiting, worded — the row's tail. */
  readonly age: (card: DeckCard) => string;
  /** The bulk menu's trigger, composed by the screen so this column owns no write. */
  readonly bulkMenu: React.ReactNode;
  /** Drawn only while a decision is held: there is nothing else to undo. */
  readonly onUndo: (() => void) | null;
  /** The reads are still open. The column holds its rows' box either way — see the reserve. */
  readonly pending: boolean;
  /** Whether the bars belong on screen this frame. `shared/lib/useSkeletonGate`'s verdict. */
  readonly skeleton: boolean;
  /** The class the rows carry when they replace a skeleton. */
  readonly reveal: string;
  /** The viewer's own preference, passed in because a CSS media block cannot reach a Motion step. */
  readonly reducedMotion: boolean;
  /** The chat glyph beside a row about a client: its conversation, in the panel over the desk. */
  readonly onOpenChat: (customerId: number) => void;
  /** The client whose conversation the panel is showing, whose row's glyph stays lit. */
  readonly talkingTo: number | null;
}

/**
 * How many rows the column reserves while it waits.
 *
 * The count a full queue shows at rest in this column's height, so the answer landing changes ink
 * and never geometry. The list scrolls past it either way.
 */
const SKELETON_ROWS = 7;

export function TaskQueueIndex({
  cards,
  selectedKey,
  scope,
  counts,
  progress,
  onScope,
  onSelect,
  age,
  bulkMenu,
  onUndo,
  pending,
  skeleton,
  reveal,
  reducedMotion,
  onOpenChat,
  talkingTo,
}: TaskQueueIndexProps) {
  const { t } = useTranslation();
  const percent = progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100);

  /**
   * The keyboard follows the selection.
   *
   * `↑ ↓` move which card is on the desk, and a selection the keyboard is not standing on is a
   * cursor the trainer cannot see and a screen reader never hears about. A decision that empties
   * the desk's controls drops focus on `<body>`, which is the other half of the same defect.
   *
   * It follows ONLY from inside the list. A version that also rescued focus from `<body>` put a
   * full ink ring around the first row on a cold load — the browser treats a programmatic focus with
   * no preceding pointer event as keyboard-driven, so `:focus-visible` matched — which reads as
   * exactly the defect this round is fixing. Nothing is lost by the narrower rule: after a mouse
   * decision the keyboard sits on the decision button, and `model/deskKeys` gives a `control` every
   * key but `Enter`; on `<body>` it gives every key including `Enter`.
   */
  const list = useRef<HTMLUListElement | null>(null);
  const selectedRow = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const row = selectedRow.current;
    const active = document.activeElement;
    if (row === null || active === null) return;
    if (list.current?.contains(active) !== true) return;
    row.focus();
  }, [selectedKey]);

  return (
    <aside className={styles.queue} aria-label={t('web:tasks.queue.title')}>
      <div className={styles.head}>
        <h2 className={styles.headTitle}>{t('web:tasks.queue.title')}</h2>
        <span className={styles.headCount}>
          {t('web:tasks.queue.progress', { done: progress.done, total: progress.total })}
        </span>
        {bulkMenu}
        {/* A `div` with the role rather than a `<progress>`: the native element cannot be styled to
          * the canon's bar in every engine. Its accessible name says the same thing the figure
          * above it does, so a reader is not told the number twice in different words. */}
        <div
          className={styles.meter}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={progress.total}
          aria-valuenow={progress.done}
          aria-label={t('web:tasks.queue.progressLabel', { done: progress.done, total: progress.total })}
        >
          <span className={styles.meterFill} style={{ width: `${String(percent)}%` }} />
        </div>

        {/* A RADIO GROUP, never a tablist: switching the scope is a choice out of three, arrows move
          * inside it, and it gets one tab stop for free. `pages/plan-library/ui/ScopeSwitch` states
          * the rule; this is the same construction. */}
        <fieldset className={styles.scope}>
          <legend className={hidden.srOnly}>{t('web:tasks.queue.scopeLegend')}</legend>
          {DECK_SCOPES.map((option) => (
            <label
              key={option}
              className={`${styles.scopeOption} ${option === scope ? styles.scopeSelected : ''}`}
            >
              <input
                type="radio"
                name="task-deck-scope"
                className={styles.scopeInput}
                value={option}
                checked={option === scope}
                onChange={() => {
                  onScope(option);
                }}
              />
              <span className={styles.scopeWord}>{t(SCOPE_LABEL_KEY[option])}</span>
              <span className={styles.scopeCount}>{counts[option]}</span>
            </label>
          ))}
        </fieldset>
      </div>

      {/* The reserve. The GATE hides the bars, never the box: the rows' height is held for the
        * whole wait, so the bars appearing is a change of ink and never of geometry. */}
      {pending ? (
        <ul className={styles.list} aria-hidden="true">
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <li className={styles.skeletonRow} key={index}>
              <span className={`${styles.barTile} ${skeleton ? skel.bar : ''}`} />
              <span className={styles.rowText}>
                <span className={`${styles.barName} ${skeleton ? skel.bar : ''}`} />
                <span className={`${styles.barReason} ${skeleton ? skel.bar : ''}`} />
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <ul className={`${styles.list} ${pending ? hidden.srOnly : reveal}`} ref={list}>
        {cards.map((card) => (
          <IndexRow
            key={card.item.key}
            card={card}
            selected={card.item.key === selectedKey}
            rowRef={card.item.key === selectedKey ? selectedRow : undefined}
            age={age}
            reducedMotion={reducedMotion}
            onSelect={onSelect}
            onOpenChat={onOpenChat}
            talking={talkingTo !== null && card.item.subjectCustomerId === talkingTo}
          />
        ))}
      </ul>
      {onUndo === null ? null : (
        <div className={styles.foot}>
          <button type="button" className={`${ghost.ghost} ${styles.undo}`} onClick={onUndo}>
            <UndoIcon className={styles.undoGlyph} />
            {t('web:tasks.queue.undo')}
            <kbd className={styles.key}>Z</kbd>
          </button>
        </div>
      )}
    </aside>
  );
}

/**
 * One row of the index.
 *
 * Its own component only so the mark's step can be a HOOK. `markIn` is keyed on the answer, and an
 * entrance bound to mount plays for every row the queue brings — eight rows entering the moment the
 * read landed, on top of the list's hand-over. `useMotionOnKeyChange` is silent for the first key a
 * row shows and plays when the answer replaces the reason's glyph, which is the only moment this
 * gesture is about.
 */
function IndexRow({
  card,
  selected,
  rowRef,
  age,
  reducedMotion,
  onSelect,
  onOpenChat,
  talking,
}: {
  readonly card: DeckCard;
  readonly selected: boolean;
  readonly rowRef: RefObject<HTMLButtonElement | null> | undefined;
  readonly age: (card: DeckCard) => string;
  readonly reducedMotion: boolean;
  readonly onSelect: (key: string) => void;
  readonly onOpenChat: (customerId: number) => void;
  readonly talking: boolean;
}) {
  // Never the trainer's own profile: nobody can write to themselves (`messageable`).
  const customerId = card.item.messageable ? card.item.subjectCustomerId : null;
  const { t } = useTranslation();
  const mark = useMotionOnKeyChange(card.handled ?? 'open', screenMotion('markIn', { reducedMotion }));

  return (
    <li className={`${styles.rowItem} ${selected ? styles.rowItemSelected : ''} ${talking ? styles.rowItemTalking : ''}`}>
      <button
        type="button"
        className={`${styles.row} ${selected ? styles.rowSelected : ''} ${
          card.handled === null ? '' : styles.rowHandled
        } ${customerId === null ? '' : styles.rowWithChat}`}
        // A row is a place to STAND on the queue, not an act — so `Enter` on one runs the
        // card's primary rather than re-selecting the row the keyboard is already on.
        // `model/deskKeys` holds the rule; this attribute is how the listener finds a row.
        {...{ [INDEX_ROW_ATTRIBUTE]: '' }}
        // The keyboard follows the selection, so a decision never drops focus on `<body>`.
        ref={rowRef}
        // The list is a set of choices and exactly one of them is shown on the desk.
        aria-current={selected ? 'true' : undefined}
        onClick={() => {
          onSelect(card.item.key);
        }}
      >
        {/* The Pulpit list's tile, the kind's own ink on a wash of it (owner, 25 September 2026). */}
        <ReasonTile
          item={card.item}
          size="sm"
          wash
          className={styles.tile}
          label={
            card.handled === null
              ? undefined
              : t(card.handled === 'snoozed' ? 'web:tasks.queue.handledSnoozed' : 'web:tasks.queue.handledDone')
          }
        >
          {/* A clock for the one card that is coming BACK; a check for both the ones that are
            * not — an answered "Gotowe" and an item the server resolved on its own.
            *
            * Keyed on the answer, so the mark REPLACING the reason's glyph plays `markIn`
            * rather than appearing: the row is the only thing on screen that says the
            * decision landed here, since the card itself has already left. The FIRST key a row
            * shows plays nothing, which is what the step comes from a hook for. */}
          <motion.span
            className={styles.tileSwap}
            key={card.handled ?? 'open'}
            initial={mark.initial}
            animate={mark.animate}
            transition={mark.transition}
          >
            {card.handled === null ? (
              <TaskGlyphMark className={styles.tileMark} glyph={card.item.glyph} />
            ) : card.handled === 'snoozed' ? (
              <ElapsedMarkIcon className={styles.tileMark} />
            ) : (
              <CheckIcon className={styles.tileMark} />
            )}
          </motion.span>
        </ReasonTile>
        <span className={styles.rowText}>
          <span className={styles.name}>{card.item.fullName}</span>
          <span className={styles.reason}>{t(card.item.titleKey)}</span>
        </span>
        <span className={styles.age}>{age(card)}</span>
      </button>
      {/* A sibling of the row, never inside it: a button may not hold another. */}
      {customerId === null ? null : (
        <button
          type="button"
          className={styles.rowChat}
          aria-label={t('web:tasks.queue.writeDm', { name: card.item.fullName })}
          title={t('web:home.action.writeDm')}
          aria-pressed={talking}
          onClick={() => {
            onOpenChat(customerId);
          }}
        >
          <BubbleGlyph size={16} />
        </button>
      )}
    </li>
  );
}
